<template>
  <card-shell
    :elevation="shellElevation"
    :inactive="shellInactive"
    :clickable="shellClickable"
    @click="handleClick"
  >
    <template #title>
      <div class="d-flex justify-space-between align-center">
        <div>
          <v-chip :color="active ? 'secondary' : undefined">{{ title }}</v-chip><template v-if="isIronman"><span class="text-caption font-weight-regular text-disabled ml-1">Ironman</span></template>
        </div>
        <div class="d-flex align-center ga-2">
          <span
            v-if="isCreator && matchAge"
            class="text-caption text-disabled"
            style="font-family: monospace"
          >{{ matchAge }}</span>
          <v-btn
          class="ml-2 pr-1"
          v-if="canDestroy && active && isCreator"
          data-test="cancel-match"
          icon
          variant="text"
          density="compact"
          size="small"
          @click.stop="$emit('destroy')"
        >
          <v-icon>mdi-trash-can-outline</v-icon>
        </v-btn>
      </div>
      </div>
    </template>
    <template v-if="teams.length >= 2">
      <div class="score-card__wrap" :class="{ 'score-card__wrap--expanded': expanded }" :style="{ minHeight: wrapMinHeight }" @transitionend="handleTransitionEnd">
        <div class="score-card__body">
          <versus-row :teams="teams" :expanded="expanded" :style="{ marginTop: rowMarginTop }" />
          <score-display
            v-if="showScore"
            :winner-name="isIronman && hasScoresBeenSubmitted ? winningTeamLabel : ''"
            :left="goals[0]"
            :right="goals[1]"
            :dash="!hasScoresBeenSubmitted"
            :hints="hints"
            :size="size"
            :margin-top="scoreMarginTop"
            :score-shadow="scoreShadow"
          />
          <div v-else>
            <div v-if="!isIronman" class="d-flex flex-column align-center" :style="{ gap: '51px', marginTop: scoreButtonsMarginTop }">
              <div
                v-for="(members, ti) in teams"
                :key="ti"
                class="d-flex flex-column align-center"
                style="gap: 4px;"
              >
                <card-score-buttons
                  :max-score="maxScoreButton[ti]"
                  :score="goals[ti]"
                  :submitting="submitting"
                  :closing="btnGroupClosing"
                  @score="(value) => { expanded = false; $emit('score', ti, value); }"
                  @close-end="handleScoreButtonsCloseEnd"
                />
              </div>
            </div>
            <div v-else class="mt-4 text-center w-100">
              <tap-winner-buttons
                :teams="teams"
                :submitting="submitting"
                :closing="btnGroupClosing"
                @select="(ti) => $emit('ironman-score', ti)"
                @close-end="handleScoreButtonsCloseEnd"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <div v-if="submitting" class="score-card__overlay">
      <div class="d-flex flex-column align-center">
        <v-progress-circular indeterminate color="primary" />
        <div class="text-caption mt-2">Submitting score...</div>
      </div>
    </div>
  </card-shell>
</template>

<script>
import CardShell from "@/components/card-shell.vue";
import VersusRow from "@/components/versus-row.vue";
import ScoreDisplay from "@/components/score-display.vue";
import CardScoreButtons from "@/components/card-score-buttons.vue";
import TapWinnerButtons from "@/components/tap-winner-buttons.vue";

export default {
  name: "ScoreCard",
  components: {
    CardShell,
    VersusRow,
    ScoreDisplay,
    CardScoreButtons,
    TapWinnerButtons
  },
  props: {
    title: {
      type: String,
      required: true
    },
    teams: {
      type: Array,
      default: () => []
    },
    goals: {
      type: Array,
      default: () => []
    },
    hints: {
      type: Array,
      default: () => []
    },
    maxScoreButton: {
      type: Array,
      default: () => []
    },
    hasScoresBeenSubmitted: {
      type: Boolean,
      default: false
    },
    active: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: true
    },
    isCreator: {
      type: Boolean,
      default: false
    },
    canDestroy: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: "text-headline-large"
    },
    scoreShadow: {
      type: String,
      default: "fade"
    },
    matchAge: {
      type: String,
      default: ""
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  emits: ["destroy", "score", "ironman-score"],
  data () {
    return {
      expanded: false,
      showScore: true,
      btnGroupClosing: false
    };
  },
  computed: {
    shellElevation () {
      return this.clickable && this.active ? 2 : 0;
    },
    shellInactive () {
      return !this.active;
    },
    shellClickable () {
      return this.clickable;
    },
    isIronman () {
      return this.teams.length >= 3;
    },
    isOnePlayerEach () {
      return this.teams[0]?.length === 1 && this.teams[1]?.length === 1;
    },
    rowMarginTop () {
      return this.isOnePlayerEach ? "0px" : "11px";
    },
    scoreButtonsMarginTop () {
      return this.isOnePlayerEach ? "-7px" : "-18px";
    },
    scoreMarginTop () {
      return this.isOnePlayerEach ? "2px" : "12px";
    },
    wrapMinHeight () {
      if (this.expanded) return "186px";
      if (this.isOnePlayerEach) return "93px";
      return "111px";
    },
    winningTeamIndex () {
      return this.goals.findIndex(g => g > 0);
    },
    winningTeam () {
      const idx = this.winningTeamIndex;
      if (idx === -1) return null;
      return { members: this.teams[idx], goals: this.goals[idx] };
    },
    winningTeamLabel () {
      if (!this.winningTeam) return "";
      return this.winningTeam.members.map(m => m.name).join(" & ");
    }
  },
  watch: {
    expanded (val) {
      if (val) {
        this.showScore = false;
        this.btnGroupClosing = false;
        this.$nextTick(() => {
          if (this.$refs.playerNames) this.$refs.playerNames.measure();
          requestAnimationFrame(() => {
            if (this.$refs.playerNamesRight) this.$refs.playerNamesRight.measure();
          });
        });
      } else {
        this.btnGroupClosing = true;
        if (this.$refs.playerNames) this.$refs.playerNames.close();
        if (this.$refs.playerNamesRight) this.$refs.playerNamesRight.close();
      }
    },
    active (val) {
      if (!val && this.expanded) {
        this.expanded = false;
      }
    }
  },
  methods: {
    handleClick () {
      if (!this.shellClickable) return;
      if (this.hasScoresBeenSubmitted && !this.isCreator) return;
      this.expanded = !this.expanded;
    },
    handleTransitionEnd (event) {
      if (event.propertyName === "min-height" && !this.expanded) {
        this.showScore = true;
      }
    },
    handleScoreButtonsCloseEnd () {
      if (!this.expanded) {
        this.showScore = true;
        this.btnGroupClosing = false;
      }
    }
  }
};
</script>

<style scoped>
.score-card__wrap {
  position: relative;
  transition: min-height .3s ease;
}
.score-card__wrap--expanded {
  min-height: 186px;
}
.score-card__body {
  position: absolute;
  inset: 0;
}
.score-card__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-surface), 0.85);
  opacity: 1;
  z-index: 10;
  border-radius: 4px;
}
</style>
